import { api, enc, qs } from '../api';
import { cents, fmtDate, fmtWhen, money, plural } from '../format';
import { go, withQuery } from '../router';
import { useApp, useLoad } from '../state';
import { useFeedback } from '../ui/feedback';
import { Area, Choice, DateField, Grid, Select, Text, words } from '../ui/fields';
import { Empty, Facts, Loading, Notes, PageHead, Ref, Sub } from '../ui/bits';
import { usePartyOpts } from './dialogs';
import { useDecide, type Item } from './Queue';
import { Avatar, HumanAvatar } from '../ui/avatar';

// Pipeline: one row per account play (a stalled deal, an expansion, a referral ask), sorted by money and staleness.
const PLAY_CODES = ['reengage', 'exec_intro', 'referral_ask', 'expansion_offer', 'case_study', 'renewal', 'win_back'];
const STATUSES = ['open', 'booked', 'won', 'lost', 'parked'];

function usePlayDialog() {
  const { openDialog, toast } = useFeedback();
  const partyOpts = usePartyOpts();
  const form = (p: Record<string, any> = {}) => (<>
    <Text name="account_name" label="Account (company)" value={p.account_name} required />
    <Grid cols={3}>
      <Text name="hubspot_deal_id" label="HubSpot deal id" value={p.hubspot_deal_id} />
      <Text name="hubspot_company_id" label="HubSpot company id" value={p.hubspot_company_id} />
      <Text name="amount" label="Amount (USD)" type="number" value={p.amount_cents != null ? p.amount_cents / 100 : ''} />
    </Grid>
    <Grid>
      <Select name="owner_party_id" label="Owner (who sends and calls)" options={partyOpts} value={p.owner_party_id} blank="Me" />
      <Select name="play_code" label="Angle" options={words(PLAY_CODES)} value={p.play_code} blank="Not chosen yet" />
    </Grid>
    <Grid>
      <Text name="mid" label="Counts toward campaign (MID)" value={p.mid} placeholder="SI.SLS.CSTMR.26.001" />
      <DateField name="next_step_due" label="Next step due" value={p.next_step_due} />
    </Grid>
    <Text name="next_step" label="Next step (one line)" value={p.next_step} />
    <Area name="reason" label="Why it stalled, or context" value={p.reason} />
  </>);
  const body = (v: Record<string, any>) => ({ ...v, amount_cents: cents(v.amount), amount: undefined, owner_party_id: v.owner_party_id || undefined, play_code: v.play_code || null, mid: v.mid || undefined });
  return {
    newPlay: (onDone: () => void) => openDialog({ title: 'Open an account play', okLabel: 'Open the play', body: form(), onSubmit: async (v) => { const r = await api('POST', '/plays', body(v)); toast(r.first_item?.created ? 'Play opened; the dossier is queued for the agent' : 'Play opened'); onDone(); } }),
    editPlay: (p: any, onDone: () => void) => openDialog({ title: `Edit ${p.account_name}`, body: (<>{form(p)}<Select name="status" label="Status" options={words(STATUSES)} value={p.status} /></>), onSubmit: async (v) => { await api('PATCH', `/plays/${enc(p.id)}`, body(v)); toast('Saved'); onDone(); } }),
  };
}

export function Pipeline({ query }: { query: Record<string, string> }) {
  const { can } = useApp();
  const { newPlay } = usePlayDialog();
  const { data, error, reload } = useLoad(() => api('GET', `/plays?${qs({ status: query.status || 'open' })}`), [query.status]);
  const rows: any[] = data?.plays ?? [];
  return (<>
    <PageHead title="Pipeline" sub={data ? `${plural(rows.length, 'account play')} · stalled deals, expansions and referral asks, each with an owner and a dated next step` : 'Account plays'}
      actions={can('marketer') && <button onClick={() => newPlay(reload)}>Open a play</button>} />
    <form className="toolbar" key={query.status} onSubmit={(ev) => { ev.preventDefault(); go(withQuery('#/pipeline', ev.currentTarget)); }}>
      <Choice name="status" options={words(['open', 'all', ...STATUSES.slice(1)])} value={query.status || 'open'} />
      <button className="quiet" type="submit">Filter</button>
    </form>
    {error ? <Empty>{error}</Empty> : !data ? <Loading /> : rows.length ? (
      <div className="card"><div className="table-wrap"><table><thead><tr><th>Account</th><th className="num">Amount</th><th>Angle</th><th>Owner</th><th>Status</th><th>Next</th><th>Last engaged</th></tr></thead><tbody>
        {rows.map((p) => (
          <tr key={p.id}>
            <td><a href={`#/plays/${p.id}`} style={{ fontWeight: 600, color: 'inherit' }}>{p.account_name}</a>{p.mid && <Sub><Ref to={`#/campaigns/${p.mid}`}>{p.mid}</Ref></Sub>}</td>
            <td className="num mono">{money(p.amount_cents)}</td>
            <td>{p.play_code ? p.play_code.replace(/_/g, ' ') : <span className="muted">not chosen</span>}</td>
            <td>{p.owner_name ? <span className="row" style={{ gap: 8 }}><HumanAvatar name={p.owner_name} size="s" /><span className="small">{p.owner_name}</span></span> : '—'}</td>
            <td><span className={`status-chip st-${p.status}`}>{p.status}</span></td>
            <td>{p.next_item ? <>{p.next_item}<Sub>{p.next_item_who || ''}{p.next_item_due ? ` · ${fmtWhen(p.next_item_due)}` : ''}</Sub></> : p.next_step ? <>{p.next_step}<Sub>{fmtDate(p.next_step_due)}</Sub></> : <span className="muted">nothing queued</span>}</td>
            <td className="nowrap muted">{fmtDate(p.last_engaged_at)}</td>
          </tr>
        ))}
      </tbody></table></div></div>
    ) : <Empty>No account plays yet. {can('marketer') && <button className="link" onClick={() => newPlay(reload)}>Open the first one</button>}</Empty>}
  </>);
}

export function PlayDetail({ id }: { id: string }) {
  const { can, me } = useApp();
  const { editPlay } = usePlayDialog();
  const decide = useDecide();
  const { data, error, reload } = useLoad(() => api('GET', `/plays/${enc(id)}`), [id]);
  if (error) return <Empty>{error}</Empty>;
  if (!data) return <Loading />;
  const { play: p, items, notes } = data as { play: any; items: Item[]; notes: any[] };
  const mine = me.party?.id && me.party.id === p.owner_party_id;
  const open = items.filter((i) => !['done', 'skipped', 'expired'].includes(i.status));
  const closed = items.filter((i) => ['done', 'skipped', 'expired'].includes(i.status));
  return (<>
    <PageHead pre={<span className={`status-chip st-${p.status}`}>{p.status}</span>} title={p.account_name}
      sub={<>{p.play_code ? p.play_code.replace(/_/g, ' ') : 'angle not chosen yet'} · {money(p.amount_cents)}{p.mid && <> · <Ref to={`#/campaigns/${p.mid}`}>{p.mid}</Ref></>}</>}
      actions={(can('manager') || mine) && <button className="quiet" onClick={() => editPlay(p, reload)}>Edit</button>} />
    <div className="cols">
      <div>
        <section><Facts items={[
          ['Owner', p.owner_name ? <span className="row" style={{ gap: 8 }}><HumanAvatar name={p.owner_name} size="s" />{p.owner_name}</span> : '—'],
          ['HubSpot deal', p.hubspot_deal_id ? <span className="mono">{p.hubspot_deal_id}</span> : '—'],
          ['HubSpot company', p.hubspot_company_id ? <span className="mono">{p.hubspot_company_id}</span> : '—'],
          ['Next step', <>{p.next_step || '—'}{p.next_step_due ? <Sub>due {fmtDate(p.next_step_due)}</Sub> : null}</>],
          ['Last engaged', fmtDate(p.last_engaged_at)],
          p.reason && ['Context', p.reason],
        ]} /></section>
        <section className="card"><div className="card-head"><h2>Steps</h2><span className="small muted">{plural(open.length, 'open step')}</span></div>
          {items.length ? (
            <table><tbody>
              {[...open, ...closed].map((i) => (
                <tr key={i.id} className={['done', 'skipped', 'expired'].includes(i.status) ? 'muted' : ''}>
                  <td style={{ width: 44 }}><Avatar agentCode={i.latest_agent_code ?? (i.actor === 'agent' ? i.stage_agent_code : null)} name={i.assignee_name} size="s" /></td>
                  <td className="title-col"><a href={`#/work/${i.id}`}>{i.stage_seq ? `${i.stage_seq}. ` : ''}{i.title}</a><Sub>{i.assignee_name || 'unassigned'} · {i.status.replace(/_/g, ' ')}{i.due_at ? ` · ${fmtWhen(i.due_at)}` : ''}</Sub></td>
                  <td className="nowrap">{i.status === 'ready_for_review' && !['done'].includes(i.status) && (can('manager') || (me.party?.id === i.reviewer_party_id)) && <button className="hue-live" onClick={() => decide(i, 'approve', reload)}>Approve</button>}
                    {(i.gate_kind === 'done' || i.gate_kind === 'none') && !['done', 'skipped', 'expired', 'ready_for_review'].includes(i.status) && (can('manager') || me.party?.id === i.assignee_party_id) && <button className="hue-live" onClick={() => decide(i, 'done', reload)}>Done</button>}</td>
                </tr>
              ))}
            </tbody></table>
          ) : <Empty>No steps yet.</Empty>}
        </section>
      </div>
      <aside className="side"><Notes notes={notes} entityType="account_play" entityId={p.id} onAdded={reload} /></aside>
    </div>
  </>);
}
